import { FC, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAgents } from '../../../hooks/useAgents';
import { useGetAgentDefinition, usePatchAgentDefinition, type AgentDefinition } from '../../../shared/api/hooks';
import { humanizeDetailText } from '../../../shared/notifications';

/**
 * Компонент для отображения снимка агента (вкладка Agent в правом сайдбаре).
 */
export const AgentSnapshot: FC<{ agentId?: string }> = ({ agentId }) => {
  const { t } = useTranslation('pages');
  const { agents } = useAgents();

  type UnknownRecord = Record<string, unknown>;
  const isRecord = (v: unknown): v is UnknownRecord => !!v && typeof v === 'object' && !Array.isArray(v);

  const activeAgent =
    (agentId ? agents?.find((a: unknown) => isRecord(a) && String(a.id) === agentId) : undefined) ||
    agents?.find((a: unknown) => isRecord(a) && Boolean(a.isRunning)) ||
    agents?.[0];

  const id = activeAgent ? String(activeAgent.id) : '';
  const defQ = useGetAgentDefinition(id);
  const patchM = usePatchAgentDefinition();

  const definition: AgentDefinition | null = (isRecord(defQ.data) ? (defQ.data as AgentDefinition) : null) ?? null;

  const [name, setName] = useState('');
  const [autostart, setAutostart] = useState(false);

  useEffect(() => {
    if (!definition) return;
    setName(definition.name ?? '');
    setAutostart(!!definition.autostart);
  }, [definition]);

  const isDirty = useMemo(() => {
    if (!definition) return false;
    return name !== (definition.name ?? '') || autostart !== !!definition.autostart;
  }, [autostart, definition, name]);

  if (!activeAgent) {
    return (
      <div className="p-4 text-center text-gray-500">
        {t('agents.snapshot.noAgents')}
      </div>
    );
  }

  const isSaving = patchM.isPending;

  const save = async () => {
    try {
      await patchM.mutateAsync({ agentId: id, patch: { name, autostart } });
      toast.success(t('agents.snapshot.saved'));
    } catch (e: unknown) {
      const msg =
        isRecord(e) && isRecord(e.response) && isRecord((e.response as UnknownRecord).data)
          ? String(((e.response as UnknownRecord).data as UnknownRecord).detail || 'Ошибка сохранения')
          : isRecord(e) && typeof e.message === 'string'
            ? e.message
            : 'Ошибка сохранения';
      toast.error(humanizeDetailText(msg, 'Не удалось сохранить настройки агента.'));
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div>
        <h3 className="text-sm font-semibold mb-2">{t('agents.snapshot.settingsTitle')}</h3>

        {defQ.isLoading ? (
          <div className="text-sm text-muted-foreground">{t('common:states.loading')}</div>
        ) : defQ.isError ? (
          <div className="text-sm text-red-400">{t('agents.snapshot.loadError')}</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2">
              <label className="text-xs text-muted-foreground">{t('agents.snapshot.nameLabel')}</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded text-sm"
                placeholder={id}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="autostart"
                type="checkbox"
                checked={autostart}
                onChange={(e) => setAutostart(e.target.checked)}
              />
              <label htmlFor="autostart" className="text-sm">
                {t('agents.snapshot.autostartLabel')}
              </label>
            </div>

            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-muted-foreground min-w-0 break-words">
                {definition ? `${definition.symbol} • ${definition.interval} • ${definition.strategyName}` : ''}
              </div>
              <button
                type="button"
                disabled={!isDirty || isSaving}
                onClick={save}
                className="shrink-0 px-3 py-1.5 rounded bg-primary text-primary-foreground text-sm disabled:opacity-50"
              >
                {isSaving ? t('agents.snapshot.saving') : t('agents.snapshot.save')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
